import { useMutationState } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useOutletContext } from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { ExpandImageButton } from '@/components/commons/expand-image-button'
import { ImageCard } from '@/components/commons/image-card'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Draggable } from '@/components/ui/dnd-atoms'
import { AlertCircleIcon, MoveIcon } from '@/components/ui/icons'
import { ListHeader } from '@/components/ui/list-header'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Skeleton } from '@/components/ui/skeleton'
import { AllocateDropdown } from '@/features/photoset/components/allocate-dropdown'
import { CompletedAllocationBox } from '@/features/photoset/components/completed-allocation-box'
import { GalleryAllocationModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-allocation-modal/gallery-allocation-modal'
import { ImagesList } from '@/features/photoset/components/images-list'
import type { HttpAllocateImagesOptions } from '@/features/photoset/http/photoset.http'
import { useAllocateImages } from '@/features/photoset/queries/use-allocate-images'
import { useDeallocateImages } from '@/features/photoset/queries/use-deallocate-images'
import { useGetPhotosetImagesList } from '@/features/photoset/queries/use-get-photoset-images-list'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import type {
	Image,
	PhotosetSpace,
	PhotosetURLParams
} from '@/features/photoset/types.ts'
import { generateToastAllocationId } from '@/features/photoset/utils'
import { handleImagesRangeSelect } from '@/features/photoset/utils.ts'
import { errorHandler } from '@/helpers/utils'
import { MUTATION_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

const AllocationImagesPoolContent = ({
	isError,
	isSuccess,
	imagesCount,
	unallocatedImagesCount,
	children
}: {
	isError: boolean
	isSuccess: boolean
	imagesCount: number
	unallocatedImagesCount: number
	children: ReactNode
}) => {
	const { t } = useTranslation()

	if (isError) {
		return (
			<ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
		)
	}
	if (isSuccess && unallocatedImagesCount === 0) {
		return <CompletedAllocationBox imagesCount={imagesCount} />
	}
	return <ScrollArea scrollbar={<Scrollbar />}>{children}</ScrollArea>
}

interface AllocationImagesPoolProps {
	currentDetailImageIndex: number | null
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
}
export const AllocationImagesPool = ({
	currentDetailImageIndex,
	setCurrentDetailImageIndex
}: AllocationImagesPoolProps) => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const images = usePhotosetStore((state) => state.images)
	const selectImages = usePhotosetStore((state) => state.selectImages)
	const unselectAll = usePhotosetStore((state) => state.unselectAll)
	const unselectImages = usePhotosetStore((state) => state.unselectImages)
	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const deallocateImages = usePhotosetStore((state) => state.deallocateImages)
	const imagesQuery = useGetPhotosetImagesList(
		{ urlParams: { photosetId: urlParams.id } },
		{ enabled: false }
	)
	const unallocatedImages = [...images.values()].filter(
		(image) => !image.space
	)
	const deallocateImagesMutation = useDeallocateImages()
	const [lastSelectedImageId, setLastSelectedImageId] = useState<
		number | null
	>(null)

	const allocateImagesMutation = useAllocateImages({
		onUndo: async (
			imagesIds: Image['id'][],
			photosetSpaceId: PhotosetSpace['id']
		) => {
			try {
				await deallocateImagesMutation.mutateAsync({
					urlParams: {
						photosetId: urlParams.id,
						photosetSpaceId
					},
					payload: selectedImages
				})

				deallocateImages({
					imagesIds,
					photosetSpaceId,
					keepSelected: true,
					trackInHistory: false
				})

				const toastId = generateToastAllocationId(
					'allocation',
					photosetSpaceId,
					imagesIds
				)
				toast.dismiss(toastId)
			} catch (error) {
				errorHandler(error)
			}
		},
		options: {
			onError: (error) => errorHandler(error)
		}
	})

	// Get all the mutation status targetized to the allocate images key
	const allocateMutationState = useMutationState({
		filters: {
			mutationKey: [MUTATION_KEYS.ALLOCATE_IMAGES],
			status: 'pending'
		},
		select: (mutation) => ({
			variables: mutation.state.variables as HttpAllocateImagesOptions
		})
	})

	// Retrieve all the image ids that are in pending allocation
	const pendingAllocationImageIds = useMemo(
		() =>
			allocateMutationState.map((allocateState) => ({
				imageIds: allocateState.variables.payload
			})),
		[allocateMutationState]
	)

	return (
		<div className='grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]'>
			<ListHeader
				endSlot={
					selectedImages.length >= 1 && (
						<div className='flex items-center gap-4'>
							<div className='flex items-center gap-2'>
								<h4 className='text-neutral-700 text-sm'>
									{t('common:x_images_selected', {
										count: selectedImages.length
									})}
								</h4>
								<ButtonLegacy
									onClick={() => {
										unselectAll()
										setLastSelectedImageId(null)
									}}
									size='sm'
									type='button'
									variant='tertiary'
								>
									{t('common:unselect_all')}
								</ButtonLegacy>
							</div>
							<AllocateDropdown
								onAllocate={({ photosetSpaceId, imagesIds }) =>
									allocateImagesMutation.mutate({
										urlParams: {
											photosetId: urlParams.id,
											photosetSpaceId
										},
										payload: imagesIds
									})
								}
							/>
						</div>
					)
				}
				size='sm'
				title={
					imagesQuery.isPending ? (
						<Skeleton height={30} width={96} />
					) : (
						t('common:x_images', {
							count: unallocatedImages.length
						})
					)
				}
			/>
			<AllocationImagesPoolContent
				imagesCount={imagesQuery.data?.length ?? 0}
				isError={imagesQuery.isError}
				isSuccess={imagesQuery.isSuccess}
				unallocatedImagesCount={unallocatedImages.length}
			>
				<ImagesList>
					{imagesQuery.isPending
						? Array.from(
								{ length: 20 },
								(_, i) => `skeleton-${i}`
							).map((key) => <ImageCard asSkeleton key={key} />)
						: unallocatedImages.map((image, index, array) => {
								const isSelected = selectedImages.some(
									(selectedImageId) =>
										selectedImageId === image.id
								)
								return (
									<Draggable
										draggingElement={
											<ImageCard
												className='relative w-[200px] cursor-grabbing rounded-xl border border-neutral-white/60 bg-neutral-white/60 p-4 shadow-xl backdrop-blur-sm'
												endSlot={
													selectedImages.length >=
														2 && (
														<div className='absolute top-0 right-0 flex h-8 w-8 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-blue-600 font-medium text-neutral-white'>
															{
																selectedImages.length
															}
														</div>
													)
												}
												image={image}
												isSelected={isSelected}
												thumbnail={image.thumbnails?.xs}
											/>
										}
										id={image.id.toString()}
										key={image.id}
									>
										{(draggable, onPointerDown) => (
											<ImageCard
												className='relative [&:not(:hover)_.show-on-hover]:hidden'
												endSlot={
													<>
														<button
															className='show-on-hover absolute top-8 left-8 cursor-grab text-neutral-300'
															type='button'
															{...draggable.listeners}
															onPointerDown={(
																event
															) => {
																if (
																	!isSelected
																) {
																	selectImages(
																		[
																			image.id
																		]
																	)
																}
																onPointerDown(
																	event
																)
															}}
															ref={
																draggable.setActivatorNodeRef
															}
														>
															<MoveIcon
																size={16}
															/>
														</button>
														<ExpandImageButton
															onClick={() => {
																setCurrentDetailImageIndex(
																	index
																)
																unselectAll()
																selectImages([
																	image.id
																])
															}}
														/>
													</>
												}
												image={image}
												isLoading={pendingAllocationImageIds.some(
													(pendingStatus) =>
														pendingStatus.imageIds.includes(
															image.id
														)
												)}
												isSelected={isSelected}
												onSelect={(isShiftPressed) => {
													handleImagesRangeSelect({
														array,
														image,
														index,
														isSelected,
														isShiftPressed,
														lastSelectedImageId,
														selectImages,
														setLastSelectedImageId,
														unselectImages
													})
												}}
												thumbnail={image.thumbnails?.xs}
											/>
										)}
									</Draggable>
								)
							})}
				</ImagesList>
			</AllocationImagesPoolContent>

			{currentDetailImageIndex !== null && (
				<GalleryAllocationModal
					currentDetailImageIndex={currentDetailImageIndex}
					onClose={() => {
						setCurrentDetailImageIndex(null)
						unselectAll()
					}}
					selectableImages={unallocatedImages}
					setCurrentDetailImageIndex={setCurrentDetailImageIndex}
				/>
			)}
		</div>
	)
}

AllocationImagesPool.displayName = 'AllocationImagesPool'
